import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Header from '../../components/Header';
import Footer from '../../components/Footer';
import Container from '../../components/Container';
import ComponentErrorBoundary from '../../components/ComponentErrorBoundary';
import SEO from '../../lib/seo';
import { auditLogApi, formatDate } from '../../lib/adminApi';

/**
 * Admin Audit Logs Page
 * @returns {React.ReactElement} Audit logs interface
 */
const AdminAuditLogs = () => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedLog, setSelectedLog] = useState(null);
  const [availableActions, setAvailableActions] = useState([]);
  const [filters, setFilters] = useState({
    action: '',
    actor_email: '',
    start_date: '',
    end_date: '',
    limit: 50,
    offset: 0,
  });
  const [appliedFilters, setAppliedFilters] = useState({
    action: '',
    actor_email: '',
    start_date: '',
    end_date: '',
    limit: 50,
    offset: 0,
  });
  const [pagination, setPagination] = useState({
    total: 0,
    hasMore: false,
  });

  useEffect(() => {
    loadLogs();
  }, [appliedFilters]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    loadAvailableActions();
  }, []); // Load actions only once on component mount

  const loadAvailableActions = async () => {
    try {
      const actions = await auditLogApi.getActions();
      setAvailableActions(actions || []);
    } catch (err) {
      console.error('Failed to load available actions:', err);
    }
  };

  const loadLogs = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const params = {};
      if (appliedFilters.action) params.action = appliedFilters.action;
      if (appliedFilters.actor_email) params.actor_email = appliedFilters.actor_email;
      if (appliedFilters.start_date) params.start_date = appliedFilters.start_date + 'T00:00:00.000Z';
      if (appliedFilters.end_date) params.end_date = appliedFilters.end_date + 'T23:59:59.999Z';
      if (appliedFilters.limit) params.limit = appliedFilters.limit;
      if (appliedFilters.offset) params.offset = appliedFilters.offset;
      
      const data = await auditLogApi.getAll(params);
      
      if (appliedFilters.offset === 0) {
        setLogs(data.logs || data);
      } else {
        setLogs(prev => [...prev, ...(data.logs || data)]);
      }
      
      setPagination({
        total: data.total || (data.logs ? data.logs.length : data.length),
        hasMore: data.hasMore || false,
      });
    } catch (err) {
      setError(err.message);
      console.error('Failed to load audit logs:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({
      ...prev,
      [key]: value,
    }));
  };

  const applyFilters = () => {
    setAppliedFilters({
      ...filters,
      offset: 0, // Reset pagination when applying new filters
    });
  };

  const clearFilters = () => {
    const clearedFilters = {
      action: '',
      actor_email: '',
      start_date: '',
      end_date: '',
      limit: 50,
      offset: 0,
    };
    setFilters(clearedFilters);
    setAppliedFilters(clearedFilters);
  };

  const setDateRange = (days) => {
    const today = new Date();
    const startDate = new Date();
    startDate.setDate(today.getDate() - days);
    
    setFilters(prev => ({
      ...prev,
      start_date: startDate.toISOString().split('T')[0],
      end_date: today.toISOString().split('T')[0],
    }));
  };

  const loadMore = () => {
    setAppliedFilters(prev => ({
      ...prev,
      offset: prev.offset + prev.limit,
    }));
  };

  const getActionIcon = (action) => {
    switch (action) {
      case 'login':
        return (
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 16l-4-4m0 0l4-4m-4 4h14m-5 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h7a3 3 0 013 3v1" />
          </svg>
        );
      case 'logout':
        return (
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
          </svg>
        );
      case 'download':
      case 'client_download_request':
      case 'client_file_downloaded':
      case 'admin_download_request':
      case 'admin_file_downloaded':
        return (
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
        );
      case 'admin_upload':
        return (
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M9 19l3 3m0 0l3-3m-3 3V10" />
          </svg>
        );
      case 'admin_add_user':
        return (
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z" />
          </svg>
        );
      default:
        return (
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
        );
    }
  };

  const getActionColor = (action) => {
    switch (action) {
      case 'login':
        return 'text-green-600 bg-green-100';
      case 'logout':
        return 'text-blue-600 bg-blue-100';
      case 'download':
      case 'client_download_request':
      case 'client_file_downloaded':
      case 'admin_download_request':
      case 'admin_file_downloaded':
        return 'text-purple-600 bg-purple-100';
      case 'admin_upload':
        return 'text-orange-600 bg-orange-100';
      case 'admin_add_user':
        return 'text-indigo-600 bg-indigo-100';
      default:
        return 'text-gray-600 bg-gray-100';
    }
  };

  const formatActionName = (action) => {
    const actionNames = {
      'login': 'Login',
      'logout': 'Logout',
      'download': 'Download',
      'client_download_request': 'Client Download Request',
      'client_file_downloaded': 'Client File Downloaded',
      'admin_download_request': 'Admin Download Request',
      'admin_file_downloaded': 'Admin File Downloaded',
      'admin_upload': 'Admin Upload',
      'admin_add_user': 'Admin Add User',
      'admin_delete_user': 'Admin Delete User',
      'admin_update_user': 'Admin Update User',
      'admin_create_client': 'Admin Create Client',
      'admin_update_client': 'Admin Update Client',
      'admin_delete_client': 'Admin Delete Client',
      'admin_create_project': 'Admin Create Project',
      'admin_update_project': 'Admin Update Project',
      'admin_delete_project': 'Admin Delete Project',
      'admin_delete_asset': 'Admin Delete Asset',
      'session_started': 'Session Started',
      'admin_r2_cleanup': 'Admin R2 Cleanup'
    };
    return actionNames[action] || action.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  const formatLogMeta = (meta) => {
    if (!meta) return null;
    try {
      const parsed = typeof meta === 'string' ? JSON.parse(meta) : meta;
      return Object.entries(parsed).map(([key, value]) => (
        <div key={key} className="text-xs">
          <span className="font-medium">{key}:</span> {String(value)}
        </div>
      ));
    } catch {
      return <div className="text-xs">{String(meta)}</div>;
    }
  };

  if (loading && logs.length === 0) {
    return (
      <div className="min-h-screen flex flex-col bg-brand-blue-600">
        <Header />
        <main className="flex-grow flex items-center justify-center">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-blue-600 mx-auto mb-4"></div>
            <p className="text-white">Loading audit logs...</p>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col bg-brand-blue-600">
      <SEO 
        title="Audit Logs | LSAS Admin Portal"
        description="View system activity and user access logs"
        url="/admin/audit-logs"
      />
      
      <Header />
      
      {/* Admin Header */}
      <section className="pt-40 pb-8 bg-brand-black text-white">
        <Container>
          <div className="flex items-center justify-between">
            <div>
              <nav className="text-sm mb-2">
                <Link to="/admin" className="text-gray-400 hover:text-white">Admin</Link>
                <span className="text-gray-400 mx-2">/</span>
                <span className="text-white">Audit Logs</span>
              </nav>
              <h1 className="text-3xl font-bold mb-2">Audit Logs</h1>
              <p className="text-gray-300">View system activity and user access logs</p>
            </div>
            <Link
              to="/admin"
              className="px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700"
            >
              ← Back to Dashboard
            </Link>
          </div>
        </Container>
      </section>

      <main className="flex-grow py-8">
        <Container>
          {error && (
            <div className="mb-6 p-4 bg-red-100 border border-red-400 text-red-700 rounded-lg">
              <p className="font-semibold">Error loading audit logs:</p>
              <p>{error}</p>
              <button 
                onClick={loadLogs}
                className="mt-2 px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
              >
                Retry
              </button>
            </div>
          )}

          {/* Filters */}
          <div className="mb-6 bg-white rounded-xl shadow-card p-4">
            <div className="flex items-center space-x-4 flex-wrap gap-2">
              <label className="text-sm font-medium text-gray-700">Filter by Action:</label>
              <select
                value={filters.action}
                onChange={(e) => handleFilterChange('action', e.target.value)}
                className="p-2 border border-gray-300 rounded-md"
              >
                <option value="">All Actions</option>
                {availableActions.map((action) => (
                  <option key={action} value={action}>
                    {formatActionName(action)}
                  </option>
                ))}
              </select>
              
              <label className="text-sm font-medium text-gray-700">Filter by User (partial match):</label>
              <input
                type="text"
                value={filters.actor_email}
                onChange={(e) => handleFilterChange('actor_email', e.target.value)}
                className="p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-brand-blue-500 focus:border-brand-blue-500"
                placeholder="e.g., @lonestaraerialsolutions.com or john"
              />
              
              <label className="text-sm font-medium text-gray-700">Start Date:</label>
              <input
                type="date"
                value={filters.start_date}
                onChange={(e) => handleFilterChange('start_date', e.target.value)}
                className="p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-brand-blue-500 focus:border-brand-blue-500"
              />
              
              <label className="text-sm font-medium text-gray-700">End Date:</label>
              <input
                type="date"
                value={filters.end_date}
                onChange={(e) => handleFilterChange('end_date', e.target.value)}
                className="p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-brand-blue-500 focus:border-brand-blue-500"
              />
              
              {/* Date Range Presets */}
              <div className="flex flex-wrap gap-1">
                <button
                  onClick={() => setDateRange(1)}
                  className="px-2 py-1 text-xs bg-gray-100 text-gray-700 rounded hover:bg-gray-200 transition-colors"
                >
                  Today
                </button>
                <button
                  onClick={() => setDateRange(7)}
                  className="px-2 py-1 text-xs bg-gray-100 text-gray-700 rounded hover:bg-gray-200 transition-colors"
                >
                  Last 7 days
                </button>
                <button
                  onClick={() => setDateRange(30)}
                  className="px-2 py-1 text-xs bg-gray-100 text-gray-700 rounded hover:bg-gray-200 transition-colors"
                >
                  Last 30 days
                </button>
                <button
                  onClick={() => setDateRange(90)}
                  className="px-2 py-1 text-xs bg-gray-100 text-gray-700 rounded hover:bg-gray-200 transition-colors"
                >
                  Last 90 days
                </button>
              </div>
              
              <label className="text-sm font-medium text-gray-700">Show:</label>
              <select
                value={filters.limit}
                onChange={(e) => handleFilterChange('limit', parseInt(e.target.value))}
                className="p-2 border border-gray-300 rounded-md"
              >
                <option value={25}>25 entries</option>
                <option value={50}>50 entries</option>
                <option value={100}>100 entries</option>
              </select>
              
              {/* Apply and Clear Buttons */}
              <div className="flex space-x-2">
                <button
                  onClick={applyFilters}
                  className="px-4 py-2 bg-brand-blue-600 text-white rounded-md hover:bg-brand-blue-700 transition-colors"
                >
                  Apply Filters
                </button>
                
                {(filters.action || filters.actor_email || filters.start_date || filters.end_date) && (
                  <button
                    onClick={clearFilters}
                    className="px-4 py-2 bg-gray-500 text-white rounded-md hover:bg-gray-600 transition-colors"
                  >
                    Clear Filters
                  </button>
                )}
              </div>
              
              {/* Show applied filters */}
              {(appliedFilters.action || appliedFilters.actor_email || appliedFilters.start_date || appliedFilters.end_date) && (
                <div className="text-xs text-gray-600 bg-gray-50 px-3 py-2 rounded-md">
                  <strong>Applied filters:</strong>
                  {appliedFilters.action && <span className="ml-2">Action: {formatActionName(appliedFilters.action)}</span>}
                  {appliedFilters.actor_email && <span className="ml-2">User: {appliedFilters.actor_email}</span>}
                  {appliedFilters.start_date && <span className="ml-2">From: {appliedFilters.start_date}</span>}
                  {appliedFilters.end_date && <span className="ml-2">To: {appliedFilters.end_date}</span>}
                </div>
              )}
            </div>
          </div>

          <div className="grid lg:grid-cols-3 gap-8">
            {/* Logs List */}
            <div className="lg:col-span-2">
              <ComponentErrorBoundary 
                fallbackMessage="Audit logs temporarily unavailable"
                componentName="AuditLogsList"
              >
                <div className="bg-white rounded-xl shadow-card">
                  <div className="p-6 border-b border-gray-200">
                    <h2 className="text-xl font-semibold">
                      Activity Log ({logs.length} entries)
                    </h2>
                  </div>
                  
                  {logs.length > 0 ? (
                    <>
                      <div className="divide-y divide-gray-200">
                        {logs.map((log) => (
                          <div 
                            key={log.id} 
                            className="p-4 hover:bg-gray-50 cursor-pointer"
                            onClick={() => setSelectedLog(log)}
                          >
                            <div className="flex items-start space-x-3">
                              <div className={`p-2 rounded-lg ${getActionColor(log.action)}`}>
                                {getActionIcon(log.action)}
                              </div>
                              
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center space-x-2">
                                  <h3 className="text-sm font-medium text-gray-900">
                                    {log.action.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}
                                  </h3>
                                  <span className="text-xs text-gray-500">
                                    {formatDate(log.ts)}
                                  </span>
                                </div>
                                <p className="text-sm text-gray-600 mt-1">
                                  {log.actor_email}
                                </p>
                                {log.resource && (
                                  <p className="text-xs text-gray-500 mt-1 truncate">
                                    Resource: {log.resource}
                                  </p>
                                )}
                              </div>
                              
                              <div className="flex-shrink-0">
                                <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
                                </svg>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                      
                      {pagination.hasMore && (
                        <div className="p-4 border-t border-gray-200 text-center">
                          <button
                            onClick={loadMore}
                            disabled={loading}
                            className="px-4 py-2 bg-brand-blue-600 text-white rounded-md hover:bg-brand-blue-700 disabled:opacity-50"
                          >
                            {loading ? 'Loading...' : 'Load More'}
                          </button>
                        </div>
                      )}
                    </>
                  ) : (
                    <div className="p-8 text-center text-gray-500">
                      <svg className="w-12 h-12 mx-auto mb-4 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                      <p>No audit logs found</p>
                      {(filters.action || filters.actor_email) && (
                        <p className="text-sm mt-2">Try adjusting your filters</p>
                      )}
                    </div>
                  )}
                </div>
              </ComponentErrorBoundary>
            </div>

            {/* Log Details */}
            <div>
              <ComponentErrorBoundary 
                fallbackMessage="Log details temporarily unavailable"
                componentName="LogDetails"
              >
                <div className="bg-white rounded-xl shadow-card">
                  <div className="p-6 border-b border-gray-200">
                    <h2 className="text-xl font-semibold">Log Details</h2>
                  </div>
                  
                  {selectedLog ? (
                    <div className="p-6">
                      <div className="flex items-center mb-4">
                        <div className={`p-3 rounded-lg mr-4 ${getActionColor(selectedLog.action)}`}>
                          {getActionIcon(selectedLog.action)}
                        </div>
                        <div>
                          <h3 className="text-lg font-medium text-gray-900">
                            {selectedLog.action.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}
                          </h3>
                          <p className="text-sm text-gray-500">
                            ID: {selectedLog.id}
                          </p>
                        </div>
                      </div>

                      <div className="space-y-4">
                        <div>
                          <p className="text-sm font-medium text-gray-700">Timestamp</p>
                          <p className="text-sm text-gray-600">{formatDate(selectedLog.ts)}</p>
                        </div>
                        
                        <div>
                          <p className="text-sm font-medium text-gray-700">Actor</p>
                          <p className="text-sm text-gray-600">{selectedLog.actor_email}</p>
                        </div>
                        
                        <div>
                          <p className="text-sm font-medium text-gray-700">Action</p>
                          <p className="text-sm text-gray-600">{selectedLog.action}</p>
                        </div>
                        
                        {selectedLog.resource && (
                          <div>
                            <p className="text-sm font-medium text-gray-700">Resource</p>
                            <p className="text-sm text-gray-600 break-all">{selectedLog.resource}</p>
                          </div>
                        )}
                        
                        {selectedLog.meta && (
                          <div>
                            <p className="text-sm font-medium text-gray-700 mb-2">Metadata</p>
                            <div className="p-3 bg-gray-50 rounded text-gray-600">
                              {formatLogMeta(selectedLog.meta)}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  ) : (
                    <div className="p-8 text-center text-gray-500">
                      <svg className="w-12 h-12 mx-auto mb-4 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      <p>Select a log entry to view details</p>
                    </div>
                  )}
                </div>
              </ComponentErrorBoundary>
            </div>
          </div>
        </Container>
      </main>

      <Footer />
    </div>
  );
};

export default AdminAuditLogs;
